import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  IconButton,
} from "@mui/material";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert, { AlertColor } from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation, gql } from "@apollo/client";

interface AddNewSimProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
}

const initialState = {
  iccid: "",
  imei: "",
};

const ADD_SIM_MUTATION = gql`
mutation AddSim($iccid:String!,$imei:String!){
addSim(iccid:$iccid,imei:$imei){
status
message
}
}
`;

const AddNewSim: React.FC<AddNewSimProps> = ({ open, onClose, onSave }) => {
  const [formData, setFormData] = useState(initialState);
  const [addSim, { loading, error }] = useMutation(ADD_SIM_MUTATION);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  React.useEffect(() => {
    if (open) {
      setFormData(initialState);
      setSnackbar({ open: false, message: '', severity: 'success' });
    }
  }, [open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value.replace(/[^a-zA-Z0-9]/g, '') }));
  };

  const handleSubmit = async () => {
    try {
      const hasChanges = formData.iccid.trim() !== '' || formData.imei.trim() !== '';
      if (!hasChanges) {
        setSnackbar({ open: true, message: 'No changes were made.', severity: 'info' });
        return;
      }
      const { data } = await addSim({
        variables: {
          iccid: formData.iccid.trim(),
          imei: formData.imei.trim()
        }
      });

      if (data?.addSim?.status === true || data?.addSim?.message === 'SIM added successfully!') {
        setSnackbar({ open: true, message: 'SIM added successfully!', severity: 'success' });
        setTimeout(() => {
          onSave(formData);
          onClose();
        }, 1000);
      } else {
        setSnackbar({ open: true, message: data?.addSim?.message || 'Failed to add SIM', severity: 'error' });
      }
    } catch (err: any) {
      console.error("Error adding SIM:", err);
      setSnackbar({ open: true, message: err.message || 'Failed to add SIM', severity: 'error' });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const isFormValid = formData.iccid.trim() !== "" && formData.imei.trim() !== "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 2,
          width: '100%',
          maxWidth: '600px',
        }
      }}
    >
      <DialogTitle sx={{
        backgroundColor: '#ffffffff',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px 24px'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Add New SIM
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: '#000',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.1)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          <Grid sx={{ xs: 12, md: 6 ,width:'45%'}}>
            <TextField
              fullWidth
              margin="normal"
              label="ICCID"
              name="iccid"
              value={formData.iccid}
              onChange={handleChange}
              required
              inputProps={{
                maxLength: 20
                
              }}
            />
          </Grid>
          <Grid sx={{ xs: 12, md: 6 ,width:'45%'}}> 
            <TextField
              fullWidth
              margin="normal"
              label="Device IMEI"
              name="imei"
              value={formData.imei}
              onChange={handleChange}
              required
              inputProps={{
                maxLength: 15
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{
        padding: '16px 24px',
        justifyContent: 'flex-end'
      }}>
        <Button
          variant="outlined"
          onClick={onClose}
        >
          CANCEL
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="error"
          disabled={!isFormValid || loading}
        >
          {loading ? 'Adding...' : 'Add SIM'}
        </Button>
      </DialogActions>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <MuiAlert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Dialog>
  );
};

export default AddNewSim;